import { unzip } from 'fflate'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { CURRENT_SCHEMA, STORE } from '~/storage/schema'
import { sha256, sha256OfText } from './checksum'
import {
  BACKUP_FORMAT,
  type BackupManifest,
  type BackupPayload,
  DATA_FILE,
  MANIFEST_FILE,
} from './format'
import { migratePayload, type PayloadMigration } from './payloadMigrations'

/**
 * Parse and validate a backup WITHOUT touching current data (D-031).
 *
 * Nothing here mutates anything. Every failure is caught before the restore is
 * allowed to proceed, because the alternative — discovering a problem half-way
 * through replacing the user's only copy — is the worst outcome this design has
 * to prevent.
 *
 * The phases below run in order, each returning either its result or a failure,
 * and are split into named functions so the sequence is readable and each step
 * can be reasoned about on its own:
 *
 *   container → manifest → completeness → version → segments
 *     → data integrity → blob integrity → payload migration
 *     → schema → counts → referential integrity
 */

export type ValidationFailure =
  | 'notAZip'
  | 'manifestMissing'
  | 'manifestUnreadable'
  | 'notASolaBackup'
  | 'incompleteBackup'
  | 'backupFormatTooNew'
  | 'dataMissing'
  | 'dataChecksumMismatch'
  | 'blobMissing'
  | 'blobChecksumMismatch'
  | 'segmentSetIncomplete'
  | 'payloadUnreadable'
  | 'schemaTooNew'
  | 'noMigrationPath'
  | 'unknownStore'
  | 'recordCountMismatch'
  | 'danglingBlobReference'

export interface ValidationError {
  readonly failure: ValidationFailure
  readonly message: string
}

export interface ValidatedBackup {
  readonly manifest: BackupManifest
  readonly payload: BackupPayload
  readonly blobs: ReadonlyMap<string, Uint8Array>
  /** True when the payload needed bringing forward from an older version. */
  readonly migrated: boolean
}

export type ValidationResult =
  | { readonly ok: true; readonly backup: ValidatedBackup }
  | { readonly ok: false; readonly error: ValidationError }

type Entries = Record<string, Uint8Array>
type Step<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ValidationError }

function fail<T>(failure: ValidationFailure, message: string): Step<T> {
  return { ok: false, error: { failure, message } }
}

function pass<T>(value: T): Step<T> {
  return { ok: true, value }
}

function unzipAsync(bytes: Uint8Array): Promise<Entries> {
  return new Promise((resolve, reject) => {
    unzip(bytes, (error, data) => (error ? reject(error) : resolve(data)))
  })
}

export interface ValidateOptions {
  readonly targetSchemaVersion?: number
  readonly migrations?: readonly PayloadMigration[]
}

/**
 * Validate a complete backup set.
 *
 * `parts` is every file the user selected. A segmented backup requires ALL of
 * its parts: a partial set is refused rather than restored incompletely.
 */
export async function validateBackup(
  parts: readonly Uint8Array[],
  options: ValidateOptions = {},
): Promise<ValidationResult> {
  const targetVersion = options.targetSchemaVersion ?? DB_SCHEMA_VERSION

  const container = await readContainer(parts)
  if (!container.ok) return container
  const entries = container.value

  const manifestStep = readManifest(entries)
  if (!manifestStep.ok) return manifestStep
  const manifest = manifestStep.value

  const acceptable = checkAcceptability(manifest, parts.length)
  if (!acceptable.ok) return acceptable

  const dataStep = await readVerifiedPayload(entries, manifest)
  if (!dataStep.ok) return dataStep

  const blobStep = await readVerifiedBlobs(entries, manifest)
  if (!blobStep.ok) return blobStep

  const migratedStep = bringForward(dataStep.value, targetVersion, options.migrations)
  if (!migratedStep.ok) return migratedStep
  const migrated = migratedStep.value

  const schemaStep = checkAgainstCurrentSchema(migrated, manifest)
  if (!schemaStep.ok) return schemaStep

  const referenceStep = checkBlobReferences(migrated, blobStep.value)
  if (!referenceStep.ok) return referenceStep

  return {
    ok: true,
    backup: {
      manifest,
      payload: migrated,
      blobs: blobStep.value,
      migrated: dataStep.value.dbSchemaVersion !== targetVersion,
    },
  }
}

async function readContainer(parts: readonly Uint8Array[]): Promise<Step<Entries>> {
  const entries: Entries = {}
  for (const part of parts) {
    try {
      Object.assign(entries, await unzipAsync(part))
    } catch {
      return fail('notAZip', 'That file is not a readable Project Sola backup. It may be damaged.')
    }
  }
  return pass(entries)
}

function readManifest(entries: Entries): Step<BackupManifest> {
  const bytes = entries[MANIFEST_FILE]
  if (bytes === undefined) {
    return fail('manifestMissing', 'This file has no backup manifest, so it cannot be verified.')
  }
  try {
    return pass(JSON.parse(new TextDecoder().decode(bytes)) as BackupManifest)
  } catch {
    return fail('manifestUnreadable', 'The backup manifest is damaged and cannot be read.')
  }
}

/** Format, completeness declaration, container version, and segment count. */
function checkAcceptability(manifest: BackupManifest, partCount: number): Step<true> {
  if (manifest.format !== BACKUP_FORMAT) {
    return fail('notASolaBackup', 'That file is not a Project Sola backup.')
  }

  // A partial artifact must never masquerade as a recovery backup.
  if (manifest.containsAllData !== true) {
    return fail(
      'incompleteBackup',
      'That file does not declare itself a complete backup, so it cannot be restored. Weekly export files are not backups.',
    )
  }

  if (manifest.backupFormatVersion > 1) {
    return fail(
      'backupFormatTooNew',
      `This backup uses a newer format than this build understands (format ${manifest.backupFormatVersion}). Update Project Sola, then restore it.`,
    )
  }

  const expected = manifest.segments?.length
  if (expected !== undefined && partCount < expected) {
    return fail(
      'segmentSetIncomplete',
      `This backup is split across ${expected} files and only ${partCount} ${partCount === 1 ? 'was' : 'were'} selected. Restoring needs every part.`,
    )
  }

  return pass(true)
}

async function readVerifiedPayload(
  entries: Entries,
  manifest: BackupManifest,
): Promise<Step<BackupPayload>> {
  const bytes = entries[DATA_FILE]
  if (bytes === undefined) {
    return fail('dataMissing', 'The backup is missing its data file, so it cannot be restored.')
  }

  const text = new TextDecoder().decode(bytes)
  if ((await sha256OfText(text)) !== manifest.dataSha256) {
    return fail(
      'dataChecksumMismatch',
      'The backup data does not match its checksum. The file has been changed or damaged since it was created, so it will not be restored.',
    )
  }

  try {
    const payload = JSON.parse(text) as BackupPayload
    if (typeof payload.dbSchemaVersion !== 'number' || typeof payload.stores !== 'object') {
      throw new Error('shape')
    }
    return pass(payload)
  } catch {
    return fail('payloadUnreadable', 'The backup data could not be read.')
  }
}

async function readVerifiedBlobs(
  entries: Entries,
  manifest: BackupManifest,
): Promise<Step<ReadonlyMap<string, Uint8Array>>> {
  const blobs = new Map<string, Uint8Array>()

  for (const entry of manifest.blobs) {
    const bytes = entries[entry.path]
    if (bytes === undefined) {
      return fail(
        'blobMissing',
        `A photo or attachment recorded in the manifest is missing from the backup${manifest.segments === undefined ? '' : '. Check that every part was selected'}.`,
      )
    }
    if (bytes.byteLength !== entry.byteSize || (await sha256(bytes)) !== entry.sha256) {
      return fail(
        'blobChecksumMismatch',
        'A photo or attachment in the backup does not match its checksum. The file has been changed or damaged, so it will not be restored.',
      )
    }
    blobs.set(entry.blobId, bytes)
  }

  return pass(blobs)
}

function bringForward(
  payload: BackupPayload,
  targetVersion: number,
  migrations: readonly PayloadMigration[] | undefined,
): Step<BackupPayload> {
  const result = migratePayload(payload, targetVersion, migrations)
  if (result.ok) return pass(result.payload)

  return fail(
    result.reason === 'newerThanSupported' ? 'schemaTooNew' : 'noMigrationPath',
    result.message,
  )
}

/** Checked against the CURRENT schema, using the MIGRATED payload. */
function checkAgainstCurrentSchema(payload: BackupPayload, manifest: BackupManifest): Step<true> {
  const knownStores = new Set<string>(CURRENT_SCHEMA.map((definition) => definition.name))

  for (const store of Object.keys(payload.stores)) {
    if (!knownStores.has(store)) {
      return fail(
        'unknownStore',
        `The backup contains data Project Sola does not recognise (${store}), so it will not be restored.`,
      )
    }
  }

  for (const [store, rows] of Object.entries(payload.stores)) {
    const expected = manifest.recordCounts[store]
    if (expected !== undefined && expected !== rows.length) {
      return fail(
        'recordCountMismatch',
        `The backup says it holds ${expected} ${store} records but contains ${rows.length}. It will not be restored.`,
      )
    }
  }

  return pass(true)
}

/** Every referenced blob must be present, or restoring would create dead links. */
function checkBlobReferences(
  payload: BackupPayload,
  blobs: ReadonlyMap<string, Uint8Array>,
): Step<true> {
  for (const store of [STORE.photos, STORE.attachments] as const) {
    for (const record of payload.stores[store] ?? []) {
      const blobId = (record as { blobId?: unknown }).blobId
      if (typeof blobId === 'string' && !blobs.has(blobId)) {
        return fail(
          'danglingBlobReference',
          'The backup refers to a photo or attachment it does not contain, so it will not be restored.',
        )
      }
    }
  }
  return pass(true)
}
